<template>
  <div class="enrolments-page">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Training Enrolments</h5>
        <p class="page-sub">Track and manage employee training enrolments across all courses</p>
      </div>
      <div class="header-actions">
        <button class="btn-export" @click="openExportModal('csv')" :disabled="exportLoading">
          <font-awesome-icon :icon="exportLoading && pendingExportFmt==='csv' ? 'spinner' : 'file-csv'" :spin="exportLoading && pendingExportFmt==='csv'" /> CSV
        </button>
        <button class="btn-export" @click="openExportModal('pdf')" :disabled="exportLoading">
          <font-awesome-icon :icon="exportLoading && pendingExportFmt==='pdf' ? 'spinner' : 'file-pdf'" :spin="exportLoading && pendingExportFmt==='pdf'" /> PDF
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filter-bar">
      <input
        v-model="filters.search"
        class="filter-input"
        placeholder="Search employee or course…"
        @input="debounceLoad"
      />
      <select v-model="filters.status" class="filter-select" @change="load">
        <option value="">All Statuses</option>
        <option value="not_started">Not Started</option>
        <option value="in_progress">In Progress</option>
        <option value="completed">Completed</option>
        <option value="overdue">Overdue</option>
        <option value="failed">Failed</option>
        <option value="pending_grading">Pending Grading</option>
        <option value="waived">Waived</option>
        <option value="withdrawn">Withdrawn</option>
      </select>
      <input v-model="filters.due_from" type="date" class="filter-input filter-input--date" @change="load" placeholder="Due from" />
      <input v-model="filters.due_to"   type="date" class="filter-input filter-input--date" @change="load" placeholder="Due to" />
      <div class="per-page-wrap">
        <label class="per-page-label">Show</label>
        <select class="per-page-select" v-model.number="length" @change="load(1)">
          <option v-for="n in [5, 10, 20, 50, 100, 200, 500, 1000]" :key="n" :value="n">{{ n }}</option>
        </select>
        <span class="per-page-label">per page</span>
      </div>
    </div>

    <!-- Table -->
    <div class="table-card">
      <div v-if="loading" class="table-loading"><div class="spinner"></div></div>

      <div v-else-if="!rows.length" class="table-empty">
        <font-awesome-icon icon="graduation-cap" class="empty-icon" />
        <p>No enrolments found.</p>
      </div>

      <table v-else class="data-table">
        <thead>
          <tr>
            <th>Employee</th>
            <th>Course</th>
            <th>Status</th>
            <th>Progress</th>
            <th>Due Date</th>
            <th>Assigned</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.id">
            <td>
              <div class="emp-name">{{ row.employee?.first_name }} {{ row.employee?.last_name }}</div>
              <div class="emp-no">{{ row.employee?.employee_no }}</div>
            </td>
            <td>
              <div class="course-title-cell">{{ row.course?.title }}</div>
              <div class="course-code">{{ row.course?.code }}</div>
            </td>
            <td><span :class="['status-pill', 'status--' + row.status]">{{ statusLabel(row.status) }}</span></td>
            <td>
              <div class="prog-wrap">
                <div class="prog-bar"><div class="prog-fill" :style="{ width: (row.progress_pct || 0) + '%' }"></div></div>
                <span class="prog-pct">{{ row.progress_pct || 0 }}%</span>
              </div>
            </td>
            <td>
              <span v-if="row.due_date" :class="['due-label', dueBadgeClass(row)]">{{ formatDate(row.due_date) }}</span>
              <span v-else class="muted">—</span>
            </td>
            <td class="muted-sm">{{ formatDate(row.assigned_at) }}</td>
            <td>
              <div class="action-wrap">
                <template v-if="row.status === 'completed'">
                  <button
                    v-if="row.certificate"
                    class="btn-action btn-action--cert"
                    :disabled="downloadingCert === row.certificate.id"
                    title="Download Certificate"
                    @click="downloadCert(row.certificate)"
                  >
                    <span v-if="downloadingCert === row.certificate.id" class="spinner-xs"></span>
                    <font-awesome-icon v-else icon="certificate" />
                  </button>
                  <button v-else class="btn-action btn-action--cert" disabled title="Certificate not yet generated"><font-awesome-icon icon="certificate" /></button>
                </template>
                <button class="btn-action" @click="openAction(row, 'extend')"    title="Extend due date" :disabled="row.status === 'completed'"><font-awesome-icon icon="calendar-plus" /></button>
                <button class="btn-action" @click="openAction(row, 'complete')"  title="Mark complete" :disabled="row.status === 'completed'"><font-awesome-icon icon="check-circle" /></button>
                <button class="btn-action btn-action--warn" @click="openAction(row, 'reset')"    title="Reset progress"><font-awesome-icon icon="redo" /></button>
                <button class="btn-action btn-action--warn" @click="openAction(row, 'waive')"    title="Waive" :disabled="['waived','withdrawn','completed'].includes(row.status)"><font-awesome-icon icon="times-circle" /></button>
                <button class="btn-action btn-action--danger" @click="openAction(row, 'withdraw')" title="Withdraw" :disabled="['waived','withdrawn'].includes(row.status)"><font-awesome-icon icon="minus-circle" /></button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="pagination.last_page > 1" class="pagination-row">
        <span class="pag-info">{{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }}</span>
        <div class="pag-btns">
          <button class="pag-btn" :disabled="pagination.current_page <= 1" @click="changePage(pagination.current_page - 1)">
            <font-awesome-icon icon="chevron-left" />
          </button>
          <button
            v-for="p in pageNumbers"
            :key="p"
            :class="['pag-btn', p === pagination.current_page ? 'pag-btn--active' : '']"
            @click="changePage(p)"
          >{{ p }}</button>
          <button class="pag-btn" :disabled="pagination.current_page >= pagination.last_page" @click="changePage(pagination.current_page + 1)">
            <font-awesome-icon icon="chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- Action Modal -->
    <div v-if="modal.open" class="modal-backdrop" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-header">
          <span class="modal-title">{{ modalTitle }}</span>
          <button class="modal-close" @click="closeModal"><font-awesome-icon icon="times" /></button>
        </div>

        <div class="modal-body">
          <div class="modal-target">
            <font-awesome-icon icon="user" class="modal-target-icon" />
            <span><strong>{{ modal.row?.employee?.first_name }} {{ modal.row?.employee?.last_name }}</strong> — {{ modal.row?.course?.title }}</span>
          </div>

          <!-- Extend: date + reason -->
          <template v-if="modal.action === 'extend'">
            <label class="form-label">New Due Date <span class="req">*</span></label>
            <input v-model="modal.dueDate" type="date" class="form-input" :min="tomorrowDate" />
          </template>

          <label class="form-label">Reason <span class="req">*</span></label>
          <textarea v-model="modal.reason" class="form-textarea" rows="3" :placeholder="reasonPlaceholder"></textarea>

          <p v-if="modal.error" class="modal-error">{{ modal.error }}</p>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" @click="closeModal">Cancel</button>
          <button :class="['btn-confirm', modalConfirmClass]" :disabled="modal.saving" @click="submitAction">
            <span v-if="modal.saving" class="spinner-sm"></span>
            <span v-else>{{ modalConfirmLabel }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Export Column Picker ── -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-cancel" @click="showExportModal = false">Cancel</button>
            <button class="btn-confirm btn-confirm--primary" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'TrainingEnrolments',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      loading: false,
      rows: [],
      pagination: {},
      length: 5,
      filters: { search: '', status: '', due_from: '', due_to: '' },
      searchTimer: null,
      showExportModal: false,
      pendingExportFmt: 'csv',
      selectedExportCols: [],
      exportLoading: false,
      exportColumnDefs: [
        { key: 'employee',    label: 'Employee',   pdfWidth: 110 },
        { key: 'employee_no', label: 'Emp No',     pdfWidth: 70 },
        { key: 'course',      label: 'Course',     pdfWidth: 140 },
        { key: 'course_code', label: 'Code',       pdfWidth: 65 },
        { key: 'status',      label: 'Status',     pdfWidth: 95 },
        { key: 'progress',    label: 'Progress',   pdfWidth: 70 },
        { key: 'due_date',    label: 'Due Date',   pdfWidth: 80 },
        { key: 'assigned_at', label: 'Assigned',   pdfWidth: 80 },
      ],

      downloadingCert: null,

      modal: {
        open: false,
        row: null,
        action: null,   // extend | complete | reset | waive | withdraw
        reason: '',
        dueDate: '',
        saving: false,
        error: '',
      },
    };
  },
  computed: {
    pageNumbers() {
      const cur  = this.pagination.current_page || 1;
      const last = this.pagination.last_page    || 1;
      const pages = [];
      for (let p = Math.max(1, cur - 2); p <= Math.min(last, cur + 2); p++) pages.push(p);
      return pages;
    },
    tomorrowDate() {
      const d = new Date(); d.setDate(d.getDate() + 1);
      return d.toISOString().slice(0, 10);
    },
    modalTitle() {
      return { extend: 'Extend Due Date', complete: 'Mark as Complete', reset: 'Reset Progress', waive: 'Waive Enrolment', withdraw: 'Withdraw Enrolment' }[this.modal.action] ?? '';
    },
    modalConfirmLabel() {
      return { extend: 'Extend', complete: 'Mark Complete', reset: 'Reset', waive: 'Waive', withdraw: 'Withdraw' }[this.modal.action] ?? 'Confirm';
    },
    modalConfirmClass() {
      return ['reset', 'waive'].includes(this.modal.action) ? 'btn-confirm--warn' : ['withdraw'].includes(this.modal.action) ? 'btn-confirm--danger' : 'btn-confirm--primary';
    },
    reasonPlaceholder() {
      return { extend: 'Reason for extending the due date…', complete: 'Reason for manual completion…', reset: 'Reason for resetting progress…', waive: 'Reason for waiving this enrolment…', withdraw: 'Reason for withdrawing this enrolment…' }[this.modal.action] ?? 'Reason…';
    },
  },
  created() {
    this.load();
  },
  methods: {
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    getRowValue(row, key) {
      switch (key) {
        case 'employee':    return (`${row.employee?.first_name || ''} ${row.employee?.last_name || ''}`).trim() || '—';
        case 'employee_no': return row.employee?.employee_no || '—';
        case 'course':      return row.course?.title || '—';
        case 'course_code': return row.course?.code || '—';
        case 'status':      return this.statusLabel(row.status);
        case 'progress':    return (row.progress_pct || 0) + '%';
        case 'due_date':    return this.formatDate(row.due_date) || '—';
        case 'assigned_at': return this.formatDate(row.assigned_at) || '—';
        default:            return '—';
      }
    },
    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const params = { page: 1, per_page: 10000 };
        if (this.filters.status)   params.status   = this.filters.status;
        if (this.filters.due_from) params.due_from = this.filters.due_from;
        if (this.filters.due_to)   params.due_to   = this.filters.due_to;
        if (this.filters.search)   params.search   = this.filters.search;
        const { data } = await axios.get('/training/enrolments', { params });
        const d    = data.data;
        const rows = d.data ?? d ?? [];
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(row => cols.map(c => this.getRowValue(row, c.key)));
        const date    = new Date().toISOString().slice(0, 10);
        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `training-enrolments-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc       = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable    = 761;
          const totalW    = cols.reduce((s, c) => s + c.pdfWidth, 0);
          const scale     = usable / totalW;
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 43, 69);
          doc.text('Training Enrolments', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(122, 144, 176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize: 7.5, cellPadding: 3.5, textColor: [26, 43, 69], overflow: 'ellipsize' },
            headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left: 40, right: 40 },
            didDrawPage(data) {
              doc.setFontSize(8); doc.setTextColor(192, 202, 214);
              doc.text(`Page ${data.pageNumber}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 16, { align: 'center' });
            },
          });
          doc.save(`training-enrolments-${date}.pdf`);
        }
      } catch { this.toast.error('Export failed. Please try again.'); }
      finally   { this.exportLoading = false; }
    },

    async load(page = 1) {
      this.loading = true;
      try {
        const params = { page, per_page: this.length };
        if (this.filters.status)   params.status   = this.filters.status;
        if (this.filters.due_from) params.due_from = this.filters.due_from;
        if (this.filters.due_to)   params.due_to   = this.filters.due_to;
        if (this.filters.search) params.search = this.filters.search;
        const { data } = await axios.get('/training/enrolments', { params });
        const d = data.data;
        const items = d.data ?? d ?? [];
        this.rows       = items;
        this.pagination = { current_page: d.current_page, last_page: d.last_page, total: d.total, from: d.from, to: d.to };
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to load enrolments.');
      } finally {
        this.loading = false;
      }
    },

    debounceLoad() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.load(), 350);
    },

    changePage(p) { this.load(p); },

    openAction(row, action) {
      this.modal = { open: true, row, action, reason: '', dueDate: '', saving: false, error: '' };
    },

    closeModal() { this.modal.open = false; },

    async submitAction() {
      const { action, row, reason, dueDate } = this.modal;
      if (!reason.trim()) { this.modal.error = 'Please provide a reason.'; return; }
      if (action === 'extend' && !dueDate) { this.modal.error = 'Please select a new due date.'; return; }

      this.modal.saving = true;
      this.modal.error  = '';

      const urlMap = {
        extend:   `/training/enrolments/${row.id}/extend`,
        complete: `/training/enrolments/${row.id}/complete-manual`,
        reset:    `/training/enrolments/${row.id}/reset`,
        waive:    `/training/enrolments/${row.id}/waive`,
        withdraw: `/training/enrolments/${row.id}/withdraw`,
      };

      const payload = { reason };
      if (action === 'extend') payload.due_date = dueDate;

      try {
        const { data } = await axios.post(urlMap[action], payload);
        this.toast.success(data.message ?? 'Done.');
        this.closeModal();
        this.load(this.pagination.current_page || 1);
      } catch (e) {
        this.modal.error = e.response?.data?.message ?? 'Action failed. Please try again.';
      } finally {
        this.modal.saving = false;
      }
    },

    statusLabel(s) {
      return { not_started: 'Not Started', in_progress: 'In Progress', completed: 'Completed', overdue: 'Overdue', failed: 'Failed', pending_grading: 'Pending Grading', waived: 'Waived', withdrawn: 'Withdrawn' }[s] ?? s;
    },

    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    async downloadCert(certificate) {
      this.downloadingCert = certificate.id;
      try {
        const { data } = await axios.get(`/training/certificates/${certificate.id}/download`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
        const a   = document.createElement('a');
        a.href     = url;
        a.download = `certificate-${certificate.certificate_no}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        this.toast.error('Failed to download certificate.');
      } finally {
        this.downloadingCert = null;
      }
    },

    dueBadgeClass(row) {
      if (row.status === 'overdue') return 'due--red';
      if (!row.due_date) return '';
      const days = Math.ceil((new Date(row.due_date) - new Date()) / 86400000);
      if (days < 0)  return 'due--red';
      if (days <= 7) return 'due--amber';
      return '';
    },
  },
};
</script>

<style scoped>
.enrolments-page { padding-bottom: 40px; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom: 20px; }
.page-title  { font-size: 18px; font-weight: 700; color: #1a2b45; margin: 0 0 4px; }
.page-sub    { font-size: 13px; color: #7a90b0; margin: 0; }
.header-actions { display:flex; gap:8px; }
.btn-export { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; white-space:nowrap; font-family:inherit; }
.btn-export:hover:not(:disabled) { background:#f4f7fb; border-color:#c8d6e8; }
.btn-export:disabled { opacity:.45; cursor:not-allowed; }

/* -- Column picker ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }

/* Filters */
.filter-bar { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 16px; }
.filter-input  { flex: 1; min-width: 180px; border: 1.5px solid #dde5f0; border-radius: 8px; padding: 8px 12px; font-size: 13px; color: #1a2b45; outline: none; }
.filter-input:focus { border-color: #004AAD; }
.filter-input--date { flex: 0 0 140px; min-width: unset; }
.filter-select { flex: 0 0 170px; border: 1.5px solid #dde5f0; border-radius: 8px; padding: 8px 12px; font-size: 13px; color: #1a2b45; background: #fff; outline: none; cursor: pointer; }
.filter-select:focus { border-color: #004AAD; }

/* Table card */
.table-card { background: #fff; border-radius: 14px; border: 1px solid #e4eaf2; overflow: hidden; }
.table-loading { display: flex; justify-content: center; padding: 60px; }
.table-empty   { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 60px 20px; color: #a0b0c8; text-align: center; }
.empty-icon    { font-size: 40px; opacity: .4; }

.data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.data-table th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 700; color: #a0b0c8; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #f0f4f8; background: #fafbfd; }
.data-table td { padding: 13px 16px; border-bottom: 1px solid #f4f6fa; vertical-align: middle; }
.data-table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover td { background: #fafbff; }

.emp-name      { font-weight: 600; color: #1a2b45; }
.emp-no        { font-size: 11px; color: #a0b0c8; margin-top: 2px; }
.course-title-cell { font-weight: 500; color: #1a2b45; max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.course-code   { font-size: 11px; color: #a0b0c8; margin-top: 2px; }
.muted         { color: #a0b0c8; }
.muted-sm      { font-size: 12px; color: #a0b0c8; }

/* Status pills */
.status-pill { padding: 3px 9px; border-radius: 10px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.status--not_started     { background: #f0f4f8; color: #7a90b0; }
.status--in_progress     { background: #e8f4ff; color: #0060b0; }
.status--completed       { background: #e8f5e9; color: #2e7d32; }
.status--overdue         { background: #fff0f0; color: #c03030; }
.status--failed          { background: #fff0f0; color: #c03030; }
.status--pending_grading { background: #fff8e6; color: #9a6000; }
.status--waived          { background: #f5f5f5; color: #7a90b0; }
.status--withdrawn       { background: #f5f5f5; color: #7a90b0; }

/* Progress bar */
.prog-wrap { display: flex; align-items: center; gap: 8px; }
.prog-bar  { flex: 1; height: 5px; background: #e4eaf2; border-radius: 3px; overflow: hidden; min-width: 60px; }
.prog-fill { height: 100%; background: #004AAD; border-radius: 3px; transition: width .3s; }
.prog-pct  { font-size: 11px; font-weight: 700; color: #5a7aaa; width: 30px; flex-shrink: 0; }

/* Due date */
.due-label { font-size: 12px; font-weight: 500; }
.due--red   { color: #c03030; }
.due--amber { color: #c07000; }

/* Action buttons */
.action-wrap { display: flex; gap: 4px; }
.btn-action { width: 28px; height: 28px; border: none; border-radius: 6px; background: #f0f4f8; color: #5a7aaa; cursor: pointer; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; }
.btn-action:hover:not(:disabled) { background: #e0eaff; color: #004AAD; }
.btn-action--warn:hover:not(:disabled) { background: #fff3e0; color: #c07000; }
.btn-action--danger:hover:not(:disabled) { background: #fff0f0; color: #c03030; }
.btn-action--cert { color: #c07000; }
.btn-action--cert:hover:not(:disabled) { background: #fef8e7; color: #c07000; }
.btn-action:disabled { opacity: .35; cursor: not-allowed; }

/* Pagination */
.pagination-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-top: 1px solid #f0f4f8; }
.pag-info { font-size: 12px; color: #a0b0c8; }
.pag-btns { display: flex; gap: 4px; }
.pag-btn  { min-width: 30px; height: 30px; border: 1.5px solid #dde5f0; border-radius: 6px; background: #fff; color: #1a2b45; font-size: 12px; cursor: pointer; padding: 0 6px; }
.pag-btn:hover:not(:disabled) { background: #f0f4f8; }
.pag-btn:disabled { opacity: .35; cursor: not-allowed; }
.pag-btn--active { background: #004AAD; color: #fff; border-color: #004AAD; }

/* Modal */
.modal-backdrop { position: fixed; inset: 0; background: rgba(10,20,40,.45); display: flex; align-items: center; justify-content: center; z-index: 200; }
.modal-box      { background: #fff; border-radius: 16px; width: 100%; max-width: 460px; box-shadow: 0 12px 40px rgba(0,0,0,.18); overflow: hidden; }
.modal-header   { display: flex; align-items: center; justify-content: space-between; padding: 18px 20px 14px; border-bottom: 1px solid #f0f4f8; }
.modal-title    { font-size: 15px; font-weight: 700; color: #1a2b45; }
.modal-close    { background: none; border: none; color: #a0b0c8; font-size: 16px; cursor: pointer; }
.modal-close:hover { color: #1a2b45; }
.modal-body     { padding: 20px; display: flex; flex-direction: column; gap: 12px; }
.modal-target   { display: flex; align-items: center; gap: 10px; background: #f8fafd; border-radius: 8px; padding: 10px 14px; font-size: 13px; color: #1a2b45; }
.modal-target-icon { color: #7a90b0; flex-shrink: 0; }
.modal-footer   { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; border-top: 1px solid #f0f4f8; }
.modal-error    { font-size: 12px; color: #e03535; margin: 0; }

.form-label    { font-size: 12px; font-weight: 700; color: #5a7aaa; }
.req           { color: #e03535; }
.form-input    { width: 100%; border: 1.5px solid #dde5f0; border-radius: 8px; padding: 9px 12px; font-size: 13px; color: #1a2b45; outline: none; }
.form-input:focus { border-color: #004AAD; }
.form-textarea { width: 100%; border: 1.5px solid #dde5f0; border-radius: 8px; padding: 9px 12px; font-size: 13px; color: #1a2b45; outline: none; resize: vertical; font-family: inherit; }
.form-textarea:focus { border-color: #004AAD; }

.btn-cancel  { background: #fff; border: 1.5px solid #dde5f0; color: #1a2b45; font-size: 13px; font-weight: 500; padding: 9px 18px; border-radius: 8px; cursor: pointer; }
.btn-cancel:hover { background: #f4f7fb; }
.btn-confirm { display: inline-flex; align-items: center; gap: 6px; border: none; font-size: 13px; font-weight: 600; padding: 9px 20px; border-radius: 8px; cursor: pointer; }
.btn-confirm:disabled { opacity: .6; cursor: not-allowed; }
.btn-confirm--primary { background: #004AAD; color: #fff; }
.btn-confirm--primary:hover:not(:disabled) { background: #0056d2; }
.btn-confirm--warn    { background: #f59e0b; color: #fff; }
.btn-confirm--warn:hover:not(:disabled)    { background: #d97706; }
.btn-confirm--danger  { background: #e03535; color: #fff; }
.btn-confirm--danger:hover:not(:disabled)  { background: #c02828; }

.per-page-wrap  { display:flex; align-items:center; gap:6px; }
.per-page-label { font-size:12px; color:#7a90b0; }
.per-page-select { height:32px; border:1.5px solid #dde5f0; border-radius:7px; padding:0 8px; font-size:12px; color:#1a2b45; background:#fff; cursor:pointer; outline:none; }
.per-page-select:focus { border-color:#004AAD; }
.spinner    { width: 32px; height: 32px; border: 3px solid #e4eaf2; border-top-color: #004AAD; border-radius: 50%; animation: spin .7s linear infinite; }
.spinner-sm { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .7s linear infinite; display: inline-block; }
.spinner-xs { width: 11px; height: 11px; border: 2px solid #e8d080; border-top-color: #c07000; border-radius: 50%; animation: spin .7s linear infinite; display: inline-block; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
